import type {RevaValueParameterType} from "@reva/shared/reva-spec";

import type {LocationInfoApiInterface} from "../api/interface/locationinfo";
import {
    LocationInfoClimateRiskApiInterface,
    LocationInfoDescriptionApiInterface,
    LocationInfoEmissionApiInterface,
    LocationInfoMicroMacroApiInterface,
    LocationInfoNoiseApiInterface,
    LocationInfoPoisApiInterface,
    LocationInfoPriceIndexApiInterface,
    LocationInfoStandardGroundValueApiInterface,
    LocationInfoTimelinesApiInterface,
} from "../api/interface/locationinfo";
import type {AvmApiLocationInfoClimateRisk} from "../api/payload-types/locationinfo-climate-risk";
import type {AvmApiLocationInfoEmission} from "../api/payload-types/locationinfo-emission";
import type {AvmApiLocationInfoMicroMacro} from "../api/payload-types/locationinfo-micro-macro";
import type {AvmApiLocationInfoNoise} from "../api/payload-types/locationinfo-noise";
import {generateDefaultQueryError} from "../util/errorUtil";
import type {StandardGroundValueParameterData} from "../util/standardGroundValueUtil";
import {selectAddressData, selectAddressParam} from "./addressSlice";
import {selectCredentials} from "./authSlice";
import {selectMostFittingGroundValue} from "./locationInfoSelectors";
import type {LocationInfoApiEndpointKey, LocationInfoApiEndpointType} from "./locationInfoSlice";
import {setEndpointData, setEndpointLoading, setEndpointQueryError} from "./locationInfoSlice";
import {
    selectAllDocProperties,
    selectSingleDocProperty,
    selectSingleParam,
    setSingleParam,
} from "./objectParametersSlice";
import {DOC_PROPERTY_POIS_PER_TYPE} from "./pdfConstants";
import {selectRevaId} from "./persistenceSlice";
import {persistMultipleDocumentProperties} from "./persistenceThunk";
import type {RevaThunkAction} from "./revaHooks";

export enum LocationInfoEndpoint {
    CLIMATE_RISK = "climateRisk",
    // #19165: löst den alten Endpunkt "microMacroText" ab
    DESCRIPTION = "description",
    EMISSION = "emission",
    NOISE = "noise",
    POIS = "pois",
    PRICE_INDEX = "priceIndex",
    STANDARD_GROUND_VALUE = "standardGroundValue",
    TIMELINES = "timelines",
}

/**
 * @param maxRank POIs je Typ; für den Fall, dass die Doc-Property noch nicht gespeichert ist
 */
export const queryLocationInfoEndpointAsync =
    (endpoint: LocationInfoEndpoint, maxRank?: number): RevaThunkAction<void> =>
    async (dispatch, getState, extraArgument): Promise<void> => {
        const {serviceApi} = extraArgument;
        const addressParam = selectAddressParam(getState());
        if (!addressParam) {
            return;
        }
        const revaId = selectRevaId(getState());

        async function runApiRequest<E extends LocationInfoApiEndpointKey>(
            reduxEndpoint: E,
            apiInterface: LocationInfoApiInterface<LocationInfoApiEndpointType<E>>,
        ) {
            if (
                !apiInterface.validateAndDispatchQueryError((queryError) =>
                    dispatch(setEndpointQueryError({endpoint, queryError})),
                )
            ) {
                return;
            }
            const credentials = selectCredentials(getState());
            if (!credentials) {
                return;
            }
            dispatch(setEndpointLoading({endpoint}));
            try {
                const response = await apiInterface.execute(serviceApi, credentials);
                if (!response) {
                    dispatch(
                        setEndpointQueryError({
                            endpoint,
                            queryError: {
                                error: "general.error.invalid",
                                errorHasContactLink: true,
                            },
                        }),
                    );
                    return;
                }
                dispatch(setEndpointData(reduxEndpoint, response));
            } catch (err) {
                dispatch(
                    setEndpointQueryError({
                        endpoint,
                        queryError: generateDefaultQueryError(err as Error),
                    }),
                );
            }
        }

        switch (endpoint) {
            case LocationInfoEndpoint.CLIMATE_RISK:
                await runApiRequest(endpoint, new LocationInfoClimateRiskApiInterface(addressParam, revaId));
                break;
            case LocationInfoEndpoint.DESCRIPTION: {
                const locationIndex = selectSingleParam(getState(), "location_index", "AVM");
                const locationIndexScaled = locationIndex != null ? Math.ceil(locationIndex / 20) : null;
                await runApiRequest(
                    endpoint,
                    new LocationInfoDescriptionApiInterface(addressParam, revaId, locationIndexScaled),
                );
                break;
            }
            case LocationInfoEndpoint.EMISSION:
                await runApiRequest(endpoint, new LocationInfoEmissionApiInterface(addressParam, revaId));
                break;
            case LocationInfoEndpoint.NOISE:
                await runApiRequest(endpoint, new LocationInfoNoiseApiInterface(addressParam, revaId));
                break;
            case LocationInfoEndpoint.POIS: {
                const maxPoisPerType = selectSingleDocProperty(getState(), DOC_PROPERTY_POIS_PER_TYPE);
                await runApiRequest(
                    endpoint,
                    new LocationInfoPoisApiInterface(addressParam, revaId, null, null, maxRank ?? maxPoisPerType ?? 1),
                );
                break;
            }
            case LocationInfoEndpoint.PRICE_INDEX:
                await runApiRequest(endpoint, new LocationInfoPriceIndexApiInterface(addressParam, revaId));
                break;
            case LocationInfoEndpoint.STANDARD_GROUND_VALUE:
                await runApiRequest(endpoint, new LocationInfoStandardGroundValueApiInterface(addressParam, revaId));
                break;
            case LocationInfoEndpoint.TIMELINES:
                await runApiRequest(endpoint, new LocationInfoTimelinesApiInterface(addressParam, revaId));
                break;
            default:
                console.error(`Unknown requested LocationInfo endpoint: ${endpoint}`);
                return;
        }
    };

/**
 * Start all address-dependent queries, after a distinct address was locked in and a Reva-ID was assigned in persistence.
 * @return {RevaThunkAction<void>}
 */
export const loadLocationInfosAsync = (): RevaThunkAction<void> => async (dispatch, getState) => {
    const addressData = selectAddressData(getState());
    if (addressData) {
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.CLIMATE_RISK));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.NOISE));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.EMISSION));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.POIS));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.STANDARD_GROUND_VALUE));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.PRICE_INDEX));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.TIMELINES));
        dispatch(queryLocationInfoEndpointAsync(LocationInfoEndpoint.DESCRIPTION));
    }
};

type CoordinateEndpointResults = {
    [LocationInfoEndpoint.CLIMATE_RISK]: AvmApiLocationInfoClimateRisk;
    [LocationInfoEndpoint.EMISSION]: AvmApiLocationInfoEmission;
    [LocationInfoEndpoint.NOISE]: AvmApiLocationInfoNoise;
    microMacro: AvmApiLocationInfoMicroMacro;
};

const coordinateApiInterfaces: {
    [E in keyof CoordinateEndpointResults]: (
        address: string,
        revaId: string | null,
    ) => LocationInfoApiInterface<CoordinateEndpointResults[E]>;
} = {
    [LocationInfoEndpoint.CLIMATE_RISK]: (address, revaId) => new LocationInfoClimateRiskApiInterface(address, revaId),
    [LocationInfoEndpoint.EMISSION]: (address, revaId) => new LocationInfoEmissionApiInterface(address, revaId),
    [LocationInfoEndpoint.NOISE]: (address, revaId) => new LocationInfoNoiseApiInterface(address, revaId),
    microMacro: (address, revaId) => new LocationInfoMicroMacroApiInterface(address, revaId),
};

/**
 * Liefert LocationInfo an einer Kartenkoordinate (Tooltips), ohne sie im Store abzulegen.
 * Ein Fehler landet dagegen schon im Store, beim gleichnamigen Endpunkt der Adresse.
 */
export const queryLocationInfoAtCoordinateAsync =
    <E extends keyof CoordinateEndpointResults>(
        latLon: [number, number],
        endpoint: E,
    ): RevaThunkAction<Promise<CoordinateEndpointResults[E] | null>> =>
    async (dispatch, getState, {serviceApi}) => {
        const credentials = selectCredentials(getState());
        if (!credentials) return null;
        const apiInterface = coordinateApiInterfaces[endpoint](`${latLon[0]}/${latLon[1]}`, selectRevaId(getState()));
        try {
            return await apiInterface.execute(serviceApi, credentials);
        } catch (err) {
            if (endpoint !== "microMacro") {
                dispatch(
                    setEndpointQueryError({
                        endpoint: endpoint as LocationInfoApiEndpointKey,
                        queryError: generateDefaultQueryError(err as Error),
                    }),
                );
            }
            return null;
        }
    };

/**
 * Initialize Document Properties for the Standard Ground Value parameters (unless there are already stored properties for this in the valuation).
 */
export const initStandardGroundValueDocPropertiesAsync = (): RevaThunkAction<void> => async (dispatch, getState) => {
    // Sobald eine DocProperty mit Präfix 'standard_ground_value' existiert, wurden diese vermutlich initialisiert oder manuell gesetzt, daher abbrechen
    const allDocProperties = selectAllDocProperties(getState());
    if (
        allDocProperties &&
        Object.entries(allDocProperties).some(([k, v]) => k.startsWith("standard_ground_value") && v)
    )
        return;
    const standardGroundValueFromInput = selectSingleParam(getState(), "standard_ground_value", "PLOT");
    const standardGroundValueDateFromInput = selectSingleParam(
        getState(),
        "standard_ground_value_valuation_date",
        "PLOT",
    );
    const bestFittingStandardGroundValue = selectMostFittingGroundValue(
        getState(),
        standardGroundValueFromInput,
        standardGroundValueDateFromInput,
    );
    if (!bestFittingStandardGroundValue) return;
    Object.values(bestFittingStandardGroundValue)
        .filter(
            (v): v is StandardGroundValueParameterData & {parameter: string; parameterType: RevaValueParameterType} =>
                !!v.parameter && v.parameterType === "DOC_PROPERTY",
        )
        //TODO hier wird noch valueDE übergeben um Freitexte für Kategorien in DocProperty zu speichern
        .forEach((v) => {
            dispatch(setSingleParam({parameter: v.parameter, parameterType: v.parameterType, value: v.valueDe}));
        });
    dispatch(persistStandardGroundDocProperties());
};

/**
 * Persist found Standard Ground Value data as Document Properties in the persistence of the REVA valuation.
 */
export const persistStandardGroundDocProperties = (): RevaThunkAction<void> => async (dispatch, getState) => {
    const allProperties = selectAllDocProperties(getState());
    if (!allProperties) return;
    const groundValueProperties = Object.fromEntries(
        Object.entries(allProperties).filter(([k]) => k.startsWith("standard_ground_value")),
    );
    if (Object.keys(groundValueProperties).length === 0) return;
    await dispatch(persistMultipleDocumentProperties(groundValueProperties));
};
